import { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Button,
    IconButton,
    TableContainer,
    Table as MuiTable,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    TablePagination
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon
} from '@mui/icons-material';
import { toast } from 'sonner';
import { promptLabService } from '../services/PromptLabService';

export function UsageCategoriesPage() {
    const [usageTypes, setUsageTypes] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [newUsageTypeName, setNewUsageTypeName] = useState('');
    const [newUsageTypeDescription, setNewUsageTypeDescription] = useState('');
    const [creating, setCreating] = useState(false);
    
    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        fetchUsageTypes();
    }, []);

    const fetchUsageTypes = async () => {
        try {
            const data = await promptLabService.getUsageTypes();
            setUsageTypes(data);
        } catch (error) {
            console.error('Failed to load usage types:', error);
            toast.error('Failed to load usage categories');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setNewUsageTypeName('');
        setNewUsageTypeDescription('');
    };

    const formatDateTime = (isoTime: string) => {
        return new Date(isoTime).toLocaleString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
        });
    };

    const handleCreateUsageType = async () => {
        if (!newUsageTypeName.trim() || !newUsageTypeDescription.trim()) {
            toast.error('Please enter both name and description.');
            return;
        }

        setCreating(true);
        try {
            const payload = {
                name: newUsageTypeName.trim(),
                description: newUsageTypeDescription.trim(),
                is_active: true
            };

            await promptLabService.createUsageType(payload);
            toast.success('Usage category created successfully!');
            handleCloseDialog();
            fetchUsageTypes();
        } catch (error: any) {
            console.error('Failed to create usage type:', error);
            toast.error(`Failed to create usage category: ${error.message || 'Unknown error'}`);
        } finally {
            setCreating(false);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Get paginated data
    const paginatedUsageTypes = usageTypes.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: 4 }}>
            <Container maxWidth="xl">
                {/* Header */}
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 700,
                                color: '#0f172a',
                                mb: 1
                            }}
                        >
                            Usage Categories
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: '#64748b'
                            }}
                        >
                            Manage usage categories for your prompts
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenDialog}
                    >
                        Add New Category
                    </Button>
                </Box>

                {/* Table */}
                <TableContainer 
                    component={Paper} 
                    elevation={0}
                    sx={{
                        border: '1px solid #e2e8f0',
                        borderRadius: 3,
                        overflow: 'hidden'
                    }}
                >
                    <MuiTable>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', py: 2 }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', py: 2 }}>Description</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', py: 2 }}>Updated By</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', py: 2 }}>Updated At</TableCell>
                                {/* <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', py: 2 }}>Actions</TableCell> */}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                                        <CircularProgress size={40} sx={{ color: '#667eea' }} />
                                    </TableCell>
                                </TableRow>
                            ) : usageTypes.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                                            No usage categories found. Click "Add New Category" to create one.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedUsageTypes.map((type: any) => (
                                    <TableRow 
                                        key={type.id}
                                        hover
                                        sx={{
                                            '&:hover': {
                                                bgcolor: '#f8fafc',
                                                cursor: 'pointer'
                                            },
                                            '&:last-child td': {
                                                borderBottom: 0
                                            }
                                        }}
                                    >
                                        <TableCell sx={{ py: 2 }}>{type.name}</TableCell>
                                        <TableCell sx={{ py: 2 }}>{type.description}</TableCell>
                                        <TableCell sx={{ py: 2 }}>
                                            <Box component="span" >{type.updated_by}</Box>
                                        </TableCell>
                                        <TableCell sx={{ py: 2 }}>
                                            <Box component="span" >{formatDateTime(type.updated_at)}</Box>
                                        </TableCell>
                                        {/* <TableCell align="right" sx={{ py: 2 }}>
                                            <IconButton
                                                size="small"
                                                sx={{ 
                                                    color: '#667eea',
                                                    '&:hover': {
                                                        bgcolor: '#eef2ff'
                                                    }
                                                }}
                                                title="Edit category"
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell> */}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </MuiTable>
                    
                    {/* Pagination */}
                    {usageTypes.length > 0 && (
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50]}
                            component="div"
                            count={usageTypes.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{
                                borderTop: '1px solid #e2e8f0',
                                bgcolor: '#f8fafc',
                                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                    color: '#64748b'
                                }
                            }}
                        />
                    )}
                </TableContainer>

                {/* Create Dialog */}
                <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>Create New Usage Category</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Name"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={newUsageTypeName}
                            onChange={(e) => setNewUsageTypeName(e.target.value)}
                            placeholder="e.g., formatting"
                            sx={{ mb: 2, mt: 1 }}
                        />
                        <TextField
                            margin="dense"
                            label="Description"
                            type="text"
                            fullWidth
                            variant="outlined"
                            multiline
                            rows={3}
                            value={newUsageTypeDescription}
                            onChange={(e) => setNewUsageTypeDescription(e.target.value)}
                            placeholder="Enter a description for this category"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog} disabled={creating}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleCreateUsageType} 
                            variant="contained"
                            disabled={creating || !newUsageTypeName.trim() || !newUsageTypeDescription.trim()}
                            startIcon={creating ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
                        >
                            {creating ? 'Creating...' : 'Create'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
}
